import type { OverageRates, Plan, PlanLimits, PlanTier } from "../types";

/**
 * Plan catalog. Mirrors orbit-platform-admin-service/src/domain/plans.ts (the
 * source of truth for limits): keep the two in sync.
 * Prices in ZAR cents (R 1,499.00 -> 149900).
 *
 * There is no user or seat limit here. Seats are unlimited on every plan.
 * The consumption allowances (voice, WhatsApp, AI tokens) are PER REP: the
 * service multiplies them by the tenant's rep count, so read a tenant's real
 * ceilings from tenant.effectiveLimits, not from these defaults.
 *
 * Overage rates kick in once a tenant exceeds the included monthly allowance.
 * They are deliberately set ~1.5x our COGS so heavy users still cover their
 * cost while staying competitive. Margins on overage:
 *   - Voice: COGS ~R0.3325/min -> overage R0.50/min (~33% margin)
 *   - WhatsApp: COGS ~R0.095/msg -> overage R0.15/msg (~37%)
 *   - AI tokens: COGS ~R0.855/1k tokens -> overage R1.30/1k (~34%)
 */
const STANDARD_OVERAGE: OverageRates = {
  voiceMinuteCents: 50,        // R 0.50 / minute
  whatsappMessageCents: 15,    // R 0.15 / message
  aiTokensPer1kCents: 130      // R 1.30 / 1,000 tokens
};

export const PLANS: Record<PlanTier, Plan> = {
  starter: {
    tier: "starter",
    monthlyPriceCents: 149900,
    defaultLimits: {
      maxNumbers: 1,
      maxCustomers: 500,
      voiceMinutesPerMonth: 500,
      whatsappMessagesPerMonth: 1000,
      aiTokensPerMonth: 50_000
    },
    overageRates: STANDARD_OVERAGE
  },
  growth: {
    tier: "growth",
    monthlyPriceCents: 399900,
    defaultLimits: {
      maxNumbers: 3,
      maxCustomers: 2500,
      voiceMinutesPerMonth: 1000,
      whatsappMessagesPerMonth: 5000,
      aiTokensPerMonth: 100_000
    },
    overageRates: STANDARD_OVERAGE
  },
  scale: {
    // Bumped from R 8,999 -> R 12,499 after profit analysis: at full plan usage
    // the previous R 8,999 left ~10% margin which one heavy month could wipe.
    // R 12,499 floors margin at ~35% at full usage, ~70% at typical 40% usage.
    tier: "scale",
    monthlyPriceCents: 1249900,
    defaultLimits: {
      maxNumbers: 10,
      maxCustomers: 10_000,
      voiceMinutesPerMonth: 2000,
      whatsappMessagesPerMonth: 20_000,
      aiTokensPerMonth: 250_000
    },
    overageRates: STANDARD_OVERAGE
  },
  enterprise: {
    tier: "enterprise",
    monthlyPriceCents: 0, // custom-quoted; floor R 85,000/mo for full-utilisation margin
    defaultLimits: {
      maxNumbers: 50,
      maxCustomers: 100_000,
      voiceMinutesPerMonth: 3_000,
      whatsappMessagesPerMonth: 5_000,
      aiTokensPerMonth: 1_000_000
    },
    overageRates: STANDARD_OVERAGE
  }
};

export function planLabel(tier: PlanTier): string {
  switch (tier) {
    case "starter": return "Starter";
    case "growth": return "Growth";
    case "scale": return "Scale";
    case "enterprise": return "Enterprise";
  }
}

/** Merge plan defaults with any tenant-specific overrides. */
export function resolveEffectiveLimits(tier: PlanTier, overrides: Partial<PlanLimits>): PlanLimits {
  return { ...PLANS[tier].defaultLimits, ...overrides };
}

export function formatZAR(cents: number): string {
  if (cents === 0) return "Custom";
  const rand = cents / 100;
  return new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(rand);
}

/**
 * Compute overage charges given current usage vs the tenant's limits (pass
 * tenant.effectiveLimits; the plan defaults are only a fallback, because the
 * per-rep allowances scale with team size). Returns ZAR cents.
 */
export function computeOverageCents(
  tier: PlanTier,
  usage: { voiceMinutes: number; whatsappMessages: number; aiTokens: number },
  limits?: PlanLimits
): { voiceCents: number; whatsappCents: number; aiCents: number; totalCents: number } {
  const plan = PLANS[tier];
  const l = limits ?? plan.defaultLimits;
  const overVoice = Math.max(0, usage.voiceMinutes - l.voiceMinutesPerMonth);
  const overWa = Math.max(0, usage.whatsappMessages - l.whatsappMessagesPerMonth);
  const overTokens = Math.max(0, usage.aiTokens - l.aiTokensPerMonth);

  const voiceCents = Math.round(overVoice * plan.overageRates.voiceMinuteCents);
  const whatsappCents = Math.round(overWa * plan.overageRates.whatsappMessageCents);
  const aiCents = Math.round((overTokens / 1000) * plan.overageRates.aiTokensPer1kCents);

  return {
    voiceCents,
    whatsappCents,
    aiCents,
    totalCents: voiceCents + whatsappCents + aiCents
  };
}
